import React, { useState, useEffect } from 'react';
import DataCollection from '../../shared/DataCollection';
import BullCardList from './BullCardList.js';
import Calves from '../../herd/Calves.js';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';
import '../../shared/DefaultBadge.css';
import './ListBulls.css';

// Inline tag editor, same commit-on-blur/Enter convention as ListHerdAnimals'
// TagCell — a free-text change is bigger/less undo-able than picking from a
// fixed list, so it shouldn't fire mid-keystroke. err.message here is
// already localized by Fetch() (server sends {"error": "bull_tag_not_found"}
// etc., translated before it ever reaches this callback).
export function BullTagCell({ bull, onSaved }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(bull.tag || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setValue(bull.tag || ''); setError(''); }, [bull.tag]);

  const commit = () => {
    const trimmed = value.trim();
    if (trimmed === (bull.tag || '')) return;
    setSaving(true);
    setError('');
    Fetch('/api/data/reproduction.bulls', 'PATCH', JSON.stringify({ _id: bull._id, tag: trimmed }),
      () => { setSaving(false); onSaved(); },
      (err) => { setSaving(false); setValue(bull.tag || ''); setError(err.message); }
    );
  };

  return (
    <div className="bull-tag-cell">
      <input
        className="bull-tag-cell-input"
        value={value}
        disabled={saving}
        placeholder={t('bull_tag')}
        onChange={e => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
      />
      {error && <span className="bull-tag-cell-error" title={error}>!</span>}
    </div>
  );
}

// Works whether or not the bull is also a registered herd animal — unlike
// HerdExpandedComponent's own Calves usage, this queries births by the
// bull's own ear tag directly, so it always shows his sired calves. Wrapped
// in the same visual "expanded panel" chrome the rest of the app uses
// (herd-expanded-style background/border), since Calves.js's own CSS
// assumes it's nested inside one rather than sitting bare in the table.
const BullExpandedComponent = ({ data: row }) => {
  const { t } = useTranslation();
  return (
    <div className="bull-expanded">
      {row.tag
        ? <Calves tag={row.tag} field="father" />
        : <div className="bull-expanded-hint">{t('bull_tag_hint_list')}</div>}
    </div>
  );
};

export const ListBulls = (props) => {
  const { t } = useTranslation();
  const [defaultBullId, setDefaultBullId] = useState('');
  const [settingsId, setSettingsId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    Fetch('/api/data/account.settings', 'GET', null,
      (data) => {
        if (data && data.length > 0) {
          setSettingsId(data[0]._id || null);
          setDefaultBullId(data[0].default_bull_id || '');
        }
      },
      () => {}
    );
  }, []);

  const handleSetDefault = (bull) => {
    const body = { default_bull_id: bull._id, default_bull_name: bull.name };
    const method = settingsId ? 'PATCH' : 'POST';
    if (settingsId) body._id = settingsId;

    Fetch('/api/data/account.settings', method, JSON.stringify(body),
      () => {
        setDefaultBullId(bull._id);
        if (!settingsId) {
          Fetch('/api/data/account.settings', 'GET', null,
            (data) => { if (data?.[0]?._id) setSettingsId(data[0]._id); },
            () => {}
          );
        }
      },
      () => {}
    );
  };

  const columns = [
    { name: t("bull_name"),   selector: row => row.name,       sortable: true },
    { name: t("bull_breed"),  selector: row => row.breed || '—', sortable: true, style: { textTransform: 'capitalize' } },
    {
      name: t("bull_tag"),
      sortable: true,
      selector: row => row.tag || '',
      cell: row => <BullTagCell bull={row} onSaved={() => setRefreshTrigger(n => n + 1)} />,
    },
    { name: t("aliases"),     selector: row => row.matches },
    { name: t("created_by"),  selector: row => row.created_by },
    {
      name: t("default"),
      width: '150px',
      cell: row => row._id === defaultBullId
        ? <span className="default-badge">★ {t("default")}</span>
        : <button className="set-default-btn" onClick={() => handleSetDefault(row)}>{t("set_as_default")}</button>
    },
  ];

  return (
    <DataCollection
      collection='reproduction.bulls'
      columns={columns}
      forceRefresh={refreshTrigger}
      expandableRows
      expandableRowsComponent={BullExpandedComponent}
      mobileComponent={(data, onRefresh) => (
        <BullCardList
          data={data}
          onRefresh={onRefresh}
          defaultBullId={defaultBullId}
          onSetDefault={handleSetDefault}
        />
      )}
    />
  );
}

export default ListBulls;
